import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { apiImageUrl } from '../utils/streamApi';

// Draw a normalized (0-1) polygon "patrol zone" over a live camera frame.
// Self-contained (mirrors the canvas math in IndustrialObjectCountPage but owns
// no shared state) so it can't regress that page. value = [[x,y],...] normalized.
//
// shape='line' draws a two-point line instead — a counting line or a boundary.
// Same canvas, same normalised output; it caps at two points and a third click
// starts a new line, because there is no such thing as a three-ended line and
// silently appending one produced a shape the backend would read as a polygon.
// Default 'poly' leaves every existing caller unchanged.
export default function PatrolZoneEditor({ cameraIdx, value = [], onChange, shape = 'poly' }) {
  const isLine = shape === 'line';
  const imgRef    = useRef(null);
  const canvasRef = useRef(null);
  const [imgUrl, setImgUrl] = useState('');
  const [dims, setDims]     = useState({ w: 0, h: 0 });
  const points = useMemo(() => (Array.isArray(value) ? value : []), [value]);

  const loadFrame = useCallback(async () => {
    if (cameraIdx === null || cameraIdx === undefined || cameraIdx === '') { setImgUrl(''); return; }
    try {
      // cache-bust so "Refresh" actually re-pulls the frame
      setImgUrl(await apiImageUrl(`/cameras/${cameraIdx}/frame?ts=${Date.now()}`));
    } catch { setImgUrl(''); }
  }, [cameraIdx]);

  useEffect(() => { loadFrame(); }, [loadFrame]);

  const sizeCanvas = useCallback(() => {
    const img = imgRef.current, cv = canvasRef.current;
    if (!img || !cv) return;
    cv.width = img.clientWidth; cv.height = img.clientHeight;
    setDims({ w: img.clientWidth, h: img.clientHeight });
  }, []);

  useEffect(() => {
    window.addEventListener('resize', sizeCanvas);
    return () => window.removeEventListener('resize', sizeCanvas);
  }, [sizeCanvas]);

  // redraw whenever the polygon or the canvas size changes
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const ctx = cv.getContext('2d');
    ctx.clearRect(0, 0, cv.width, cv.height);
    if (!points.length) return;
    ctx.lineWidth = isLine ? 3 : 2;
    ctx.strokeStyle = '#4da3ff';
    ctx.fillStyle = 'rgba(77,163,255,0.18)';
    ctx.beginPath();
    points.forEach(([x, y], i) => {
      const px = x * cv.width, py = y * cv.height;
      if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
    });
    if (!isLine && points.length >= 3) ctx.closePath();
    if (!isLine) ctx.fill();
    ctx.stroke();
    ctx.fillStyle = '#4da3ff';
    points.forEach(([x, y]) => {
      ctx.beginPath();
      ctx.arc(x * cv.width, y * cv.height, 4, 0, Math.PI * 2);
      ctx.fill();
    });
  }, [points, dims, isLine]);

  const handleClick = e => {
    const cv = canvasRef.current;
    if (!cv) return;
    const rect = cv.getBoundingClientRect();
    const clamp = v => Math.min(1, Math.max(0, +v.toFixed(4)));
    const x = clamp((e.clientX - rect.left) / rect.width);
    const y = clamp((e.clientY - rect.top) / rect.height);
    if (isLine && points.length >= 2) { onChange?.([[x, y]]); return; }   // start again
    onChange?.([...points, [x, y]]);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <button type="button" onClick={loadFrame} style={btn}>↻ Refresh frame</button>
        <button type="button" onClick={() => onChange?.(points.slice(0, -1))} disabled={!points.length} style={btn}>⤺ Undo point</button>
        <button type="button" onClick={() => onChange?.([])} disabled={!points.length} style={btn}>✕ Clear</button>
        <span style={{ color: 'var(--text-muted)', fontSize: 13, alignSelf: 'center' }}>
          {isLine
            ? (points.length < 2
                ? `Click the two ends of the line (${points.length} of 2 placed).`
                : 'Line placed — click again anywhere to start a new one.')
            : `${points.length} point${points.length === 1 ? '' : 's'} — click the image to add. 3+ points make a zone.`}
        </span>
      </div>
      <div style={{ position: 'relative', width: '100%', maxWidth: 640, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', background: '#000' }}>
        {imgUrl ? (
          <img ref={imgRef} src={imgUrl} alt="camera frame" onLoad={sizeCanvas}
               style={{ display: 'block', width: '100%', height: 'auto' }} />
        ) : (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Select a camera to load its frame.</div>
        )}
        <canvas ref={canvasRef} onClick={handleClick}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', cursor: imgUrl ? 'crosshair' : 'default' }} />
      </div>
    </div>
  );
}

const btn = {
  padding: '6px 12px', background: 'var(--surface-alt)', color: 'var(--text)',
  border: '1px solid var(--border)', borderRadius: 6, cursor: 'pointer', fontSize: 13,
};
